import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PlusCircle, MapPin, PartyPopper, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { DashboardFilters, EMPTY_FILTERS, applyFilters, hasActiveFilters } from "../components/dashboard/DashboardFilters";
import { StatsCard, CandidatesAlertCard, UpcomingEventsCard, CandidaturesCard, countByStatus, upcomingEventsOf, withCandidates } from "../components/dashboard/DashboardCards";
import { RequestRow } from "../components/dashboard/RequestRow";
import { StruttureCard } from "../components/dashboard/StruttureCard";
import { useAuth } from "../context/AuthContext";

const FILTERS_KEY = "nexus.dashboard.filters";
const loadFilters = () => {
  try { return { ...EMPTY_FILTERS, ...JSON.parse(sessionStorage.getItem(FILTERS_KEY) || "{}") }; } catch { return EMPTY_FILTERS; }
};

const useRequestsPolling = () => {
  const [requests, setRequests] = useState(null);
  const [newCandidates, setNewCandidates] = useState({});
  const prevRef = useRef(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/requests");
    const prev = prevRef.current;
    if (prev) {
      const fresh = {};
      data.forEach((r) => {
        const n = (r.candidates || []).length;
        if (n > (prev[r.id] ?? 0) && r.status === "pubblicata") {
          fresh[r.id] = n - (prev[r.id] ?? 0);
          toast.info(`Nuova candidatura per ${r.code}`, { description: `${n} partner candidat${n === 1 ? "o" : "i"} — clicca per scegliere`, duration: 8000 });
        }
      });
      if (Object.keys(fresh).length) setNewCandidates((s) => ({ ...s, ...fresh }));
    }
    prevRef.current = Object.fromEntries(data.map((r) => [r.id, (r.candidates || []).length]));
    setRequests(data);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  return { requests, setRequests, newCandidates, setNewCandidates, load };
};

const useRowActions = (load, setRequests) => {
  const patchRow = (id, patch) => setRequests((prev) => (prev || []).map((x) => (x.id === id ? { ...x, ...patch } : x)));
  return {
    toggleFatturato: async (r) => {
      try {
        const { data } = await api.patch(`/requests/${r.id}/fatturato`, { fatturato: !r.fatturato });
        patchRow(r.id, { fatturato: data.fatturato, status: data.status || r.status });
        toast.success(data.fatturato ? `${r.code} segnata come fatturata${data.status === "completata" && r.status === "da_fatturare" ? " · stato Completato" : ""}` : `${r.code} da fatturare`);
      } catch { toast.error("Impossibile aggiornare lo stato di fatturazione"); }
    },
    changeStatus: async (r, status) => {
      if (status === r.status) return;
      let motivo = "";
      if (status === "annullata") {
        const m = window.prompt("Motivo annullamento (facoltativo). Il partner assegnato verrà avvisato via email:", "");
        if (m === null) return;
        motivo = m;
      }
      if (status === "pubblicata" && r.assigned_partner_id && !window.confirm(`Riportare ${r.code} a Pubblicata? Il partner assegnato verrà rimosso e la corsa tornerà disponibile.`)) return;
      try { await api.patch(`/requests/${r.id}/status`, { status, motivo }); toast.success(`${r.code}: stato aggiornato`); await load(); }
      catch (err) { toast.error("Impossibile cambiare stato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    },
    deleteRequest: async (e, r) => {
      e.stopPropagation();
      if (!window.confirm(`Eliminare la richiesta ${r.code}? L'operazione è irreversibile.`)) return;
      try { await api.delete(`/requests/${r.id}`); toast.success(`Richiesta ${r.code} eliminata`); await load(); }
      catch { toast.error("Impossibile eliminare la richiesta"); }
    },
    sollecita: async (e, r) => {
      e.stopPropagation();
      try {
        const { data } = await api.post(`/requests/${r.id}/sollecita`);
        patchRow(r.id, { last_sollecito_at: data.last_sollecito_at });
        toast.success(`Sollecito inviato per ${r.code}`, { description: `${data.partners_notified} partner avvisati via email` });
      } catch (err) { toast.error("Sollecito non inviato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    },
    downloadPdf: async (e, r) => {
      e.stopPropagation();
      try {
        const res = await api.get(`/requests/${r.id}/pdf`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url; a.download = `Foglio_Servizio_${(r.code || "").replace("/", "_")}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      } catch { toast.error("Modulo non disponibile"); }
    },
  };
};

export default function OperativaDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { requests, setRequests, newCandidates, setNewCandidates, load } = useRequestsPolling();
  const [filters, setFilters] = useState(loadFilters);
  useEffect(() => { sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); }, [filters]);
  const actions = useRowActions(load, setRequests);

  const upcomingEvents = upcomingEventsOf(requests);
  const totalNewCandidates = Object.values(newCandidates).reduce((a, b) => a + b, 0);
  const richiedentiOptions = [...new Set((requests || []).map((r) => r.operative?.provenienza).filter(Boolean))].sort();
  const filtered = applyFilters(requests, filters);

  return (
    <AppLayout>
      <div className="nx-fade-in max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight">Centrale Operativa</h1>
            <p className="text-sm text-slate-500 mt-1">Gestione richieste di trasporto sanitario</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="new-request-button" onClick={() => navigate("/operativa/nuova")} className="gap-2"><PlusCircle className="h-4 w-4" /> Nuova Richiesta</Button>
            <Button data-testid="new-evento-button" variant="outline" onClick={() => navigate("/operativa/nuovo-evento")} className="gap-2"><PartyPopper className="h-4 w-4" /> Nuovo Evento</Button>
            <Button data-testid="import-excel-button" variant="outline" onClick={() => navigate("/operativa/importa")} className="gap-2" title="Importazione massiva da Excel"><FileSpreadsheet className="h-4 w-4" /> Importa Excel</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <StatsCard counts={countByStatus(requests)} />
          <StruttureCard onOpen={(r) => navigate(`/operativa/strutture/richieste/${r.id}`)} />
          <CandidaturesCard items={withCandidates(requests)} onOpen={(r) => navigate(`/operativa/richiesta/${r.id}`)} />
          {totalNewCandidates > 0 && <CandidatesAlertCard total={totalNewCandidates} onOpen={() => setFilters((f) => ({ ...f, status: "pubblicata" }))} onDismiss={() => setNewCandidates({})} />}
          <UpcomingEventsCard events={upcomingEvents} onOpen={(r) => navigate(`/operativa/richiesta/${r.id}`)} onSollecita={actions.sollecita} onFilterEvents={() => setFilters((f) => ({ ...f, type: "evento" }))} />
        </div>

        <Card className="border-slate-200 overflow-hidden">
          <DashboardFilters filters={filters} setFilters={setFilters} richiedentiOptions={richiedentiOptions} />
          {requests === null ? (
            <div className="p-5 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={`skeleton-${i}`} className="h-12 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <MapPin className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">{hasActiveFilters(filters) ? "Nessuna richiesta corrisponde ai filtri." : "Nessuna richiesta creata. Crea la prima richiesta di trasporto."}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[130px]">Codice</TableHead><TableHead className="min-w-[160px]">Paziente / Evento</TableHead><TableHead className="min-w-[170px]">Tratta</TableHead>
                  <TableHead className="w-[130px] whitespace-nowrap">Data / Ora</TableHead><TableHead className="min-w-[140px]">Richiedente</TableHead><TableHead className="w-[150px]">Stato</TableHead>
                  <TableHead className="min-w-[120px]">Partner</TableHead><TableHead className="w-[90px] text-center">Fatturato</TableHead><TableHead className="w-[150px] text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{filtered.map((r) => <RequestRow key={r.id} r={r} isNew={!!newCandidates[r.id]} navigate={navigate} actions={actions} canDelete={user?.staff_role === "amministratore"} />)}</TableBody>
            </Table>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
